// Utilities for the redesigned Mixed Query editor (V2).
// These helpers are intentionally decoupled from the legacy GenericMixedQueryTab
// so the new dedicated page can reuse the exact same data contract without
// importing the large legacy component.
import { getConfig } from "../../../config/runtimeConfig";

export const DEFAULT_SUBQUERY_QUERY = "SELECT * FROM <>";

// Default data_pull_query per comparison sub-type, mirroring the legacy
// MixedQueryTab.handleTypeChange. Used by the V2 hook when the user selects one
// of the comparison-only sub-types in the Comparison Research Query flow.
export const SUBQUERY_DEFAULT_QUERIES: Record<string, string> = {
  Merged_Data: "SELECT * FROM <MERGED_DATA>",
  Missing_Data: "SELECT * FROM <MISSING>",
  Mismatched_Data: "SELECT * FROM <MISMATCHED>",
};

// Sub-types that are backed by the rule's merge results (comparison flow only).
export const MERGE_SUB_TYPES = [
  "Merged_Data",
  "Missing_Data",
  "Mismatched_Data",
];

export const MAX_SUBQUERIES = process.env
  .REACT_APP_RESEARCH_QUERY_MAXIMUM_SUB_QUERY
  ? parseInt(getConfig("REACT_APP_RESEARCH_QUERY_MAXIMUM_SUB_QUERY"))
  : 3;

// Shape of a single sub-query (data source) row used by the V2 editor.
export interface IMixedSubQueryV2 {
  id: number;
  type: string;
  sub_type:
    | "Resource"
    | "External"
    | "Merged_Data"
    | "Missing_Data"
    | "Mismatched_Data"
    | "";
  resource_id: number | null;
  resource_code?: string | null;
  linked_service_id: number | null;
  linked_service_code?: string | null;
  connection_key_id: number | null;
  connection_key_code?: string | null;
  table_name: string;
  external_source_name: string;
  connection_details: any[];
  data_pull_query: string;
  dependency_list: string[];
  dependency_marker: string;
  available_dependencies?: string[];
  create_temp_table_index: boolean;
  create_permanent_table: boolean;
  is_active: boolean;
  external_columns?: { name: string; value: string }[];
}

// Prefix of the generated dependency markers persisted on the `source` items.
export const DEPENDENCY_MARKER_PREFIX = "Sub_Query_";

// Matches both the canonical `Sub_Query_12` marker and the legacy
// space-separated `Sub Query 12` form so historical records still parse.
const DEPENDENCY_MARKER_PATTERN = /^sub[_ ]query[_ ](\d+)$/;

// Normalizes a persisted marker to the canonical underscore form. Legacy
// space-separated markers ("Sub Query 2") become "Sub_Query_2"; anything else is
// only trimmed so custom user-provided names are left untouched.
export const normalizeDependencyMarker = (value: any): string => {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  const parsed = DEPENDENCY_MARKER_PATTERN.exec(trimmed.toLowerCase());
  if (!parsed) return trimmed;
  return `${DEPENDENCY_MARKER_PREFIX}${parseInt(parsed[1], 10)}`;
};

// Returns the numeric suffix of a dependency marker, or null when the marker is
// not in the generated `Sub_Query_N` shape (custom or legacy-unparseable names).
export const parseDependencyMarkerIndex = (value: any): number | null => {
  if (typeof value !== "string") return null;
  const parsed = DEPENDENCY_MARKER_PATTERN.exec(value.trim().toLowerCase());
  if (!parsed) return null;
  const index = parseInt(parsed[1], 10);
  return Number.isFinite(index) ? index : null;
};

// Shared allocator for a new, never-reused dependency marker. The next marker is
// derived from the highest parseable numeric suffix among the current markers
// (NOT from the sub-query count), so deleting a middle sub-query and adding a
// new one can never resurrect an existing marker. Unparseable/legacy markers are
// ignored for the numeric derivation but still respected for uniqueness.
export const getNextDependencyMarker = (
  subQueries: Pick<IMixedSubQueryV2, "dependency_marker">[] | undefined,
): string => {
  const items = subQueries || [];
  const used = new Set(
    items
      .map((item) => normalizeDependencyMarker(item?.dependency_marker))
      .filter(Boolean)
      .map((marker) => marker.toLowerCase()),
  );
  const maxIndex = items.reduce((max, item) => {
    const parsed = parseDependencyMarkerIndex(item?.dependency_marker);
    return parsed !== null && parsed > max ? parsed : max;
  }, 0);
  let next = maxIndex + 1;
  while (used.has(`${DEPENDENCY_MARKER_PREFIX}${next}`.toLowerCase())) {
    next += 1;
  }
  return `${DEPENDENCY_MARKER_PREFIX}${next}`;
};

// Next unique positional UI id (React key / cross-reference key in the editor
// dialog). Derived from the highest existing id so removing a middle row and
// adding a new one cannot produce a duplicate id either.
export const getNextSubQueryId = (
  subQueries: Pick<IMixedSubQueryV2, "id">[] | undefined,
): number =>
  (subQueries || []).reduce(
    (max, item) => (typeof item?.id === "number" && item.id > max ? item.id : max),
    0,
  ) + 1;

// Creates a blank sub-query with the given 1-based UI id. Defaults the sub-query
// type to "Resource" so a newly added data source starts with "Resource Queries"
// selected (matching the legacy builder's default). `dependencyMarker` decouples
// the stable backend identity from the positional id: callers that already hold
// the existing sub-queries pass an allocated marker, while the positional
// fallback is kept for standalone/blank usages.
export const createEmptySubQuery = (
  index: number,
  dependencyMarker?: string,
): IMixedSubQueryV2 => ({
  id: index,
  type: "Resource",
  sub_type: "Resource",
  resource_id: null,
  resource_code: null,
  linked_service_id: null,
  linked_service_code: null,
  connection_key_id: null,
  connection_key_code: null,
  table_name: "",
  external_source_name: "",
  connection_details: [],
  data_pull_query: DEFAULT_SUBQUERY_QUERY,
  dependency_list: [],
  dependency_marker:
    dependencyMarker || `${DEPENDENCY_MARKER_PREFIX}${index}`,
  available_dependencies: [],
  create_temp_table_index: false,
  create_permanent_table: false,
  is_active: true,
});

// Resolves the final dependency_marker for every persisted `source` item.
// Persisted markers are preserved as-is (only whitespace/legacy normalization is
// applied) because they are the identity referenced by dependency_list and by
// the `<marker>` placeholders inside the SQL. Missing markers and collisions are
// repaired defensively: the FIRST occurrence of a duplicated marker keeps the
// persisted value and later occurrences receive a freshly allocated marker.
//
// Inherent ambiguity: when legacy data contains duplicate markers, an existing
// dependency_list / SQL reference to that marker cannot be attributed to a
// specific source. Keeping the first occurrence is the safest resolution because
// Keeping the first occurrence provides deterministic backward-compatible
// resolution for existing references, which are intentionally left untouched.
const resolvePersistedMarkers = (source: any[]): string[] => {
  const normalized = source.map((item: any) =>
    normalizeDependencyMarker(item?.dependency_marker),
  );
  const used = new Set<string>();
  let counter = normalized.reduce((max, marker) => {
    const parsed = parseDependencyMarkerIndex(marker);
    return parsed !== null && parsed > max ? parsed : max;
  }, 0);
  const allocate = (): string => {
    counter += 1;
    while (used.has(`${DEPENDENCY_MARKER_PREFIX}${counter}`.toLowerCase())) {
      counter += 1;
    }
    return `${DEPENDENCY_MARKER_PREFIX}${counter}`;
  };
  return normalized.map((marker) => {
    const key = marker.toLowerCase();
    const resolved = !marker || used.has(key) ? allocate() : marker;
    used.add(resolved.toLowerCase());
    return resolved;
  });
};

// Maps the `source` array of an existing edit-form record into the V2 sub-query
// shape, mirroring the legacy mapping in GenericMixedQueryTab (connection_details
// resolution + available_dependencies derivation). Markers are never regenerated
// positionally; see resolvePersistedMarkers for the defensive repair rules.
export const mapSourceToSubQueries = (
  source: any[],
  linkedServicesData: any[] | undefined,
): IMixedSubQueryV2[] => {
  if (!Array.isArray(source) || source.length === 0) return [];
  const markers = resolvePersistedMarkers(source);
  // Maps each persisted marker (as written) to its resolved marker so existing
  // dependency_list references survive normalization. First occurrence wins,
  // which is why a duplicated legacy marker resolves to the earliest holder.
  const markerAliases = new Map<string, string>();
  source.forEach((item: any, index: number) => {
    const raw = String(item?.dependency_marker ?? "").trim().toLowerCase();
    if (raw && !markerAliases.has(raw)) markerAliases.set(raw, markers[index]);
  });
  return source.map((item: any, index: number) => {
    let connectionDetails = item.connection_details || [];
    if (
      item.linked_service_id &&
      (!connectionDetails || connectionDetails.length === 0)
    ) {
      const linkedService = linkedServicesData?.find(
        (ls: any) => ls.id === item.linked_service_id,
      );
      if (linkedService?.connection_details?.connection_keys) {
        connectionDetails = linkedService.connection_details.connection_keys;
      }
    }
    return {
      ...item,
      id: index + 1,
      connection_details: connectionDetails,
      dependency_marker: markers[index],
      dependency_list: (item.dependency_list || []).map(
        (dep: any) =>
          markerAliases.get(String(dep ?? "").trim().toLowerCase()) ?? dep,
      ),
      available_dependencies: markers.filter(
        (_, candidateIndex) =>
          candidateIndex !== index && source[candidateIndex]?.is_active !== false,
      ),
      is_active: item.is_active ?? true,
    } as IMixedSubQueryV2;
  });
};

// Builds the `source` array persisted for a Mixed research query, matching the
// legacy multipleSource construction (Step 7 of handleSaveDialogQuery).
export const buildMixedSource = (subQueries: IMixedSubQueryV2[]): any[] =>
  subQueries.map(({ external_columns, available_dependencies, ...item }) => ({
    ...item,
    type: "Mixed",
    sub_type: item.sub_type,
    resource_id: item.resource_id,
    resource_code: item.resource_code,
    linked_service_id: item.linked_service_id,
    linked_service_code: item.linked_service_code,
    connection_key_id: item.connection_key_id,
    connection_key_code: item.connection_key_code,
    external_source_name: item.external_source_name,
    data_pull_query: item.data_pull_query,
    dependency_marker: item.dependency_marker,
    create_temp_table_index: item.create_temp_table_index || false,
    create_permanent_table: item.create_permanent_table || false,
    is_active: item.is_active !== false,
  }));

// Hydrates the shared `columns` state with the Resource-type columns for the
// given sub-queries, mirroring the legacy GenericMixedQueryTab.fetchResourceColumns
// so the Available Columns chips are populated on open. `fetchColumnsByIds` is
// the resourcesService.getResourceColumnsByMultipleIds function (injected to keep
// this util free of service imports).
export const hydrateResourceColumns = async (
  source: any[],
  resourcesData: any[],
  setColumns: React.Dispatch<React.SetStateAction<any>>,
  fetchColumnsByIds: (ids: number[]) => Promise<any[]>,
): Promise<void> => {
  if (!Array.isArray(source) || source.length === 0) return;
  const resourceIds = source.map((item) => item.resource_id).filter(Boolean);
  const matchedResources = (resourcesData || []).filter((resource: any) =>
    resourceIds.includes(resource.id),
  );
  const matchedResourceColumnData = matchedResources
    .map((resource: any) => {
      const columnId =
        resource?.additional_properties?.resource_column_details_id;
      if (!columnId) return null;
      return {
        resourceColumnId: columnId,
        name: resource?.resource_name,
        resourceId: resource?.id,
        resourceCode: resource?.code,
      };
    })
    .filter(Boolean) as any[];
  const matchedResourceColumnIds = matchedResourceColumnData.map(
    (item) => item.resourceColumnId,
  );
  if (matchedResourceColumnIds.length === 0) return;
  const result = await fetchColumnsByIds(matchedResourceColumnIds);
  const resultMap = new Map(
    (result || []).map((item: any) => [
      item?.id,
      (item?.resource_column_properties?.resource_columns || []).map(
        (col: any) => ({ name: col?.column_name, value: col?.column_name }),
      ),
    ]),
  );
  const columnsByResourceId = matchedResourceColumnData.reduce(
    (acc: any, item) => {
      acc[item.resourceId] = {
        name: item.resourceCode,
        columns: resultMap.get(item.resourceColumnId) || [],
      };
      return acc;
    },
    {},
  );
  setColumns((prev: any) => ({
    ...prev,
    mixedQueryData: {
      ...(prev?.mixedQueryData || {}),
      ...columnsByResourceId,
    },
    resourceColumns: matchedResourceColumnData.map((item) => ({
      name: item.resourceCode,
      columns: columnsByResourceId[item?.resourceId]?.columns,
    })),
    usedResourceIds: matchedResourceColumnData.map((item) => item.resourceId),
  }));
};

// Hydrates the shared `columns` state for a single newly-selected Resource,
// merging into the existing entries so the other sub-queries' Available Columns
// keep their chips. Used when a Resource is picked in a Mixed Query sub-query,
// where hydrateResourceColumns' replace semantics would drop the other sources.
export const hydrateResourceColumnsForResource = async (
  resourceId: number,
  resourcesData: any[],
  setColumns: React.Dispatch<React.SetStateAction<any>>,
  fetchColumnsByIds: (ids: number[]) => Promise<any[]>,
): Promise<void> => {
  if (resourceId === null || resourceId === undefined) return;
  const resource = (resourcesData || []).find(
    (item: any) => item?.id === resourceId,
  );
  const resourceColumnId =
    resource?.additional_properties?.resource_column_details_id;
  if (!resourceColumnId) return;
  const result = await fetchColumnsByIds([resourceColumnId]);
  const matched = (result || []).find(
    (item: any) => item?.id === resourceColumnId,
  );
  const resourceColumns = (
    matched?.resource_column_properties?.resource_columns || []
  ).map((col: any) => ({ name: col?.column_name, value: col?.column_name }));
  setColumns((prev: any) => ({
    ...prev,
    mixedQueryData: {
      ...(prev?.mixedQueryData || {}),
      [resourceId]: {
        name: resource?.code,
        columns: resourceColumns,
      },
    },
    usedResourceIds: (prev?.usedResourceIds || []).includes(resourceId)
      ? prev?.usedResourceIds
      : [...(prev?.usedResourceIds || []), resourceId],
  }));
};

// Returns a user-friendly display label for a sub-query. dependency_marker is a
// backend key only and is intentionally not used here.
export const getSubQueryLabel = (
  item: IMixedSubQueryV2,
  idx: number,
): string => {
  const fallback = `Sub Query ${idx + 1}`;
  if (item?.sub_type === "External") {
    return item?.external_source_name?.trim() || fallback;
  }
  if (item?.sub_type === "Resource") {
    return (
      item?.external_source_name?.trim() ||
      item?.resource_code?.trim() ||
      fallback
    );
  }
  if (item?.sub_type && MERGE_SUB_TYPES.includes(item.sub_type)) {
    return item?.external_source_name?.trim() || fallback;
  }
  return fallback;
};

// Returns the Available Column names for a source, derived from the shared
// `columns` state. Resource sources read from columns.mixedQueryData keyed by
// resource id; External sources read from the item's fetched external_columns.
export const getColumnNamesForSource = (
  item: IMixedSubQueryV2,
  columns: any,
): string[] => {
  if (item?.sub_type === "Resource" && item?.resource_id != null) {
    const entry = columns?.mixedQueryData?.[item.resource_id];
    return (entry?.columns || []).map((c: any) => c?.name).filter(Boolean);
  }
  if (item?.sub_type === "External") {
    const external = (item as any)?.external_columns || [];
    if (external.length > 0) {
      return external.map((c: any) => c?.name).filter(Boolean);
    }
    const fetched = columns?.fetchedColumns || [];
    const matched = fetched.find((f: any) => f?.name === item?.table_name);
    return (matched?.columns || []).map((c: any) => c?.name).filter(Boolean);
  }
  if (item?.sub_type && MERGE_SUB_TYPES.includes(item.sub_type)) {
    const source =
      item.sub_type === "Mismatched_Data"
        ? columns?.mismatchedColumns
        : columns?.missingColumns;
    return (source || []).map((c: any) => c?.name).filter(Boolean);
  }
  return [];
};

// Scoped `columns` object for a single comparison sub-query card (Merged_Data /
// Missing_Data / Mismatched_Data). Merged/Missing read from the rule-derived
// missingColumns; Mismatched reads from mismatchedColumns. Keyed under
// `mergedColumns` for the merged_query chips branch (matches the legacy tab).
export const buildMergeScopedColumns = (
  item: IMixedSubQueryV2,
  columns: any,
): any => ({
  mergedColumns:
    (item?.sub_type === "Mismatched_Data"
      ? columns?.mismatchedColumns
      : columns?.missingColumns) || [],
});

// Scoped `columns` object for a single Resource sub-query card: only that
// resource's columns, keyed for the resource_query chips branch.
export const buildResourceScopedColumns = (
  item: IMixedSubQueryV2,
  columns: any,
): any => ({
  resourceColumns: [
    {
      name: item?.resource_code || "",
      columns:
        columns?.mixedQueryData?.[item?.resource_id as any]?.columns || [],
    },
  ],
});

// Scoped `columns` object for a single External sub-query card: only that
// item's fetched columns, keyed for the external_query chips branch.
export const buildExternalScopedColumns = (item: IMixedSubQueryV2): any => ({
  fetchedColumns: item?.external_columns?.length
    ? [{ name: item?.table_name || "", columns: item.external_columns }]
    : [],
});

// Scoped `columns` object for the Main Query panel: aggregates ALL sub-queries
// (active or inactive) into a mixedQueryData-shaped object for the main_query
// branch. Resource sources key by resource_id; External sources are injected
// under a stable synthetic key so they also appear as tabs. `usedResourceIds`
// is populated for every key because ResourceColumnsChips gates mixedQueryData
// entries on Number(key) membership in that list.
export const buildMainQueryScopedColumns = (
  subQueries: IMixedSubQueryV2[],
  columns: any,
): any => {
  const mixedQueryData: Record<string, { name: string; columns: any[] }> = {};
  (subQueries || []).forEach((item, index) => {
    if (item?.sub_type === "Resource" && item?.resource_id != null) {
      const entry = columns?.mixedQueryData?.[item.resource_id];
      mixedQueryData[String(item.resource_id)] = {
        name: entry?.name || item?.resource_code || `Source ${index + 1}`,
        columns: entry?.columns || [],
      };
    } else if (item?.sub_type === "External") {
      mixedQueryData[`ext_${index}`] = {
        name:
          item?.external_source_name ||
          item?.table_name ||
          `Source ${index + 1}`,
        columns: item?.external_columns || [],
      };
    } else if (item?.sub_type && MERGE_SUB_TYPES.includes(item.sub_type)) {
      // Comparison sub-types have no own resource; surface the rule-derived
      // merged/missing/mismatched columns under a synthetic key so they still
      // appear as a Main Query tab.
      mixedQueryData[`merge_${index}`] = {
        name:
          item?.external_source_name || `${item.sub_type.replace("_", " ")}`,
        columns:
          (item.sub_type === "Mismatched_Data"
            ? columns?.mismatchedColumns
            : columns?.missingColumns) || [],
      };
    }
  });
  const usedResourceIds = Object.keys(mixedQueryData).map((k) => Number(k));
  return { mixedQueryData, usedResourceIds };
};

// Validates that a proposed dependency_marker is unique (case-insensitive) and
// contains only alphanumerics/underscores. Returns an error string or null.
export const validateDependencyMarker = (
  value: string,
  index: number,
  subQueries: IMixedSubQueryV2[],
): string | null => {
  if (value.includes(" ")) return "Spaces are not allowed in Sub Query Name";
  if (/[^a-zA-Z0-9_]/.test(value))
    return "Only letters, numbers and underscores are allowed in Sub Query Name";
  if (value && value.trim()) {
    const dup = subQueries.some(
      (it, i) =>
        i !== index &&
        it.dependency_marker?.toLowerCase().trim() ===
          value.toLowerCase().trim(),
    );
    if (dup)
      return "Sub Query Name already exists. Please provide a unique name.";
  }
  return null;
};

// Matches the `<token>` placeholders used inside the SQL to reference another
// sub-query's result set (e.g. "SELECT * FROM <Sub_Query_2>").
const SOURCE_PLACEHOLDER_REGEX = /<\s*([A-Za-z0-9_ ]+?)\s*>/g;

// Collects the placeholder tokens of a query that no longer resolve to a live
// sub-query. A token is reported only when it is unambiguously a sub-query
// reference: either it was removed during this editing session, or it is in the
// generated `Sub_Query_N` shape (which is reserved and therefore cannot be a
// resource code or one of the comparison result-table tokens).
const findDanglingMarkersInQuery = (
  query: string | undefined,
  liveMarkers: Set<string>,
  removedMarkers: Set<string>,
): string[] => {
  if (!query) return [];
  const dangling: string[] = [];
  const seen = new Set<string>();
  let match = SOURCE_PLACEHOLDER_REGEX.exec(query);
  while (match !== null) {
    const token = normalizeDependencyMarker(match[1]);
    const key = token.toLowerCase();
    if (
      token &&
      !seen.has(key) &&
      !liveMarkers.has(key) &&
      (removedMarkers.has(key) || parseDependencyMarkerIndex(token) !== null)
    ) {
      seen.add(key);
      dangling.push(token);
    }
    match = SOURCE_PLACEHOLDER_REGEX.exec(query);
  }
  SOURCE_PLACEHOLDER_REGEX.lastIndex = 0;
  return dangling;
};

// Finds every query that still references a sub-query marker which no longer
// exists (typically after deleting a middle data source). Results are keyed by
// sub-query index, with -1 used for the Main Query, matching the
// MixedQueryV2Errors keying so the warning can be surfaced on the right editor.
export const findDanglingDependencyReferences = (
  subQueries: IMixedSubQueryV2[] | undefined,
  mainQuery: string | undefined,
  removedMarkers: string[] = [],
): { index: number; markers: string[] }[] => {
  const items = subQueries || [];
  const liveMarkers = new Set(
    items
      .map((item) => normalizeDependencyMarker(item?.dependency_marker))
      .filter(Boolean)
      .map((marker) => marker.toLowerCase()),
  );
  const removed = new Set(
    removedMarkers
      .map((marker) => normalizeDependencyMarker(marker))
      .filter((marker) => marker && !liveMarkers.has(marker.toLowerCase()))
      .map((marker) => marker.toLowerCase()),
  );
  const results: { index: number; markers: string[] }[] = [];
  items.forEach((item, index) => {
    const markers = findDanglingMarkersInQuery(
      item?.data_pull_query,
      liveMarkers,
      removed,
    );
    if (markers.length > 0) results.push({ index, markers });
  });
  const mainMarkers = findDanglingMarkersInQuery(
    mainQuery,
    liveMarkers,
    removed,
  );
  if (mainMarkers.length > 0) results.push({ index: -1, markers: mainMarkers });
  return results;
};

// Message surfaced on a query editor whose SQL still references removed
// sub-queries.
export const buildDanglingDependencyMessage = (markers: string[]): string =>
  `Query references removed sub ${
    markers.length > 1 ? "queries" : "query"
  }: ${markers.join(", ")}. Please update the query.`;

// Resolves whether the Mixed Query V2 editor should be read-only. It is always
// read-only in view-only contexts. During execution it is read-only unless the
// embedding page explicitly opts in to execution-time overrides, in which case
// every field becomes editable (overrides apply to the execution payload only
// and are never persisted).
export const resolveMixedQueryReadOnly = (params: {
  isViewOnly?: boolean;
  isFromExecution?: boolean;
  allowExecutionOverride?: boolean;
}): boolean => {
  const { isViewOnly, isFromExecution, allowExecutionOverride } = params;
  return Boolean(isViewOnly || (isFromExecution && !allowExecutionOverride));
};
